/**
 * HW Product UX — product page helpers (VERSION 1.6 - Design Refresh)
 *
 *   A. Option cards   — tap-friendly cards built from the theme's own
 *                       <select> pickers (full text, price differences,
 *                       sold-out state). The select stays in the page and
 *                       remains the source of truth.
 *   B. Order checklist — "3 of 9 complete · Next: VIN" above Add to Cart on
 *                       products that need information or an acknowledgment.
 *   C. Sticky buy bar  — price + Add to Cart follow the customer once the
 *                       main button scrolls away; it never bypasses a gate.
 *   D. Savings tag     — keeps "$20 Off" tidy when the variant changes.
 *
 * IMPORTANT: this file never adds to the cart, never changes a price and
 * never enables the Add to Cart button. It only (1) sets the value of the
 * existing select and fires the same `change` event a customer would, and
 * (2) clicks the existing Add to Cart button. Core / freight / tune charge
 * gates, WCP dealer pricing and the tuning order fields keep working exactly
 * as they did.
 */
(function () {
  'use strict';

  if (window.__hwProductUx) return;
  window.__hwProductUx = true;

  var doc = document;
  var reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function $(sel, scope) { return (scope || doc).querySelector(sel); }
  function $$(sel, scope) { return Array.prototype.slice.call((scope || doc).querySelectorAll(sel)); }
  function ready(fn) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', fn);
    else fn();
  }
  function text(el) { return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : ''; }
  function el(tag, cls, html) {
    var n = doc.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ── money ───────────────────────────────────────────────────────────────── */
  function money(cents) {
    var fmt = (window.theme && theme.settings && theme.settings.moneyFormat) || '${{amount}}';
    var neg = cents < 0;
    var n = Math.abs(cents) / 100;
    function withDelims(precision, thousands, decimal) {
      var parts = n.toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return withDelims(0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(0, '.', ',');
        default: return withDelims(2, ',', '.');
      }
    });
    out = out.replace(/<[^>]*>/g, '').replace(/[.,]00(?!\d)/, '');
    return (neg ? '−' : '') + out;
  }

  /* ── the main product on the page ────────────────────────────────────────── */
  function mainProduct() {
    var all = $$('product-component.product-section, .product-section[data-section-type="product"]');
    for (var i = 0; i < all.length; i++) {
      if (all[i].getAttribute('data-modal') !== 'true') return all[i];
    }
    return null;
  }

  // The theme's product element is wrapped in a lazy "island": when it wakes
  // up, the placeholder element is swapped for the real <product-component>
  // and its children are moved across. Never hold on to the element itself —
  // always ask for the one that is currently in the page.
  var liveEl = null;
  function live() {
    if (liveEl && liveEl.isConnected) return liveEl;
    liveEl = mainProduct();
    return liveEl || document.createElement('div');
  }

  function variantsOf(container) {
    var node = $('[data-variant-json]', container);
    if (!node) return [];
    var list;
    try { list = JSON.parse(node.value || node.textContent || '[]'); } catch (e) { return []; }
    // WCP wholesale app: dealer prices live in a JSON blob keyed by variant id.
    // Mirror the lookup the theme's own price update uses so the differences
    // shown on the cards match the price the customer will actually see.
    list.forEach(function (v) {
      var blob = doc.getElementsByClassName('wcp_json_' + v.id)[0];
      if (!blob) return;
      try {
        var data = JSON.parse(blob.innerText || blob.textContent);
        (data.variants || []).forEach(function (w) {
          if (w.id === v.id && typeof w.price === 'number') v.price = w.price;
        });
      } catch (e) {}
    });
    return list;
  }

  /* =========================================================================
     A. OPTION CARDS
     ========================================================================= */
  function OptionCards(container) {
    this.variants = variantsOf(container);
    this.groups = [];
    this.hydrated = false;
    this.dirty = [];
    this.build();
    if (!this.groups.length) return;

    var self = this;
    doc.addEventListener('variant:change', function () { self.hydrated = true; self.sync(); });
    doc.addEventListener('change', function (e) {
      if (e.target && e.target.tagName === 'SELECT' && live().contains(e.target)) self.sync();
    });
    // The theme's product script loads lazily. If the customer picks an option
    // before it is ready, replay that change once it is so price / variant
    // update exactly as they would have.
    doc.addEventListener('product-component:loaded', function () {
      window.setTimeout(function () {
        self.hydrated = true;
        var pending = self.dirty.slice();
        self.dirty = [];
        pending.forEach(function (select) {
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
        self.sync();
      }, 60);
    });
    // availability flags (option.disabled) are set by the theme after load
    window.setTimeout(function () { self.sync(); }, 400);
    window.setTimeout(function () { self.sync(); }, 1600);
  }

  OptionCards.prototype.eligible = function (select) {
    if (!select || select.hasAttribute('data-hw-native')) return false;
    if (select.multiple || select.options.length < 1) return false;
    // "Select…" placeholder selects are order questions the customer must
    // answer; they stay native so the browser's own required-field prompt works.
    if (select.required) return false;
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value === '') return false;
    }
    return true;
  };

  OptionCards.prototype.build = function () {
    var self = this;
    var wrappers = $$('.variant-wrapper', live());
    var labelled = wrappers.filter(function (w) {
      var l = $('.variant__label', w);
      return l && !l.classList.contains('hidden-label');
    });
    var showSteps = labelled.length > 1;

    wrappers.forEach(function (wrapper) {
      if (wrapper.classList.contains('hw-has-opt')) return;
      var select = $('select', wrapper);
      var stepNo = labelled.indexOf(wrapper) + 1;
      var label = $('.variant__label', wrapper);

      // Step number + sentence-style label apply to every picker in the card
      if (label && !label.classList.contains('hidden-label') && !label.closest('.hw-opt-head')) {
        var labelText = text(label);
        if (labelText.length > 34 || /\?\s*$/.test(labelText)) label.classList.add('hw-q');
        if (showSteps && stepNo > 0) {
          var head = el('div', 'hw-opt-head');
          label.parentNode.insertBefore(head, label);
          var step = el('span', 'hw-opt-step');
          step.setAttribute('aria-hidden', 'true');
          step.textContent = stepNo;
          head.appendChild(step);
          head.appendChild(label);
          wrapper.classList.add('hw-has-step');
        }
      }

      if (!self.eligible(select)) return;
      self.groups.push(self.buildGroup(wrapper, select, label));
    });
  };

  OptionCards.prototype.buildGroup = function (wrapper, select, label) {
    var self = this;
    var opts = Array.prototype.slice.call(select.options);
    var maxLen = opts.reduce(function (m, o) { return Math.max(m, text(o).length); }, 0);
    var n = opts.length;

    var mode = 'list';
    if (maxLen <= 12) mode = 'pills';
    else if (maxLen <= 26 && n <= 6) mode = 'two';

    var collapsible = (mode === 'pills') ? n > 14 : n > 6;
    if (collapsible && mode === 'two') mode = 'list';

    var list = el('div', 'hw-opt hw-opt--' + mode);
    list.setAttribute('role', 'radiogroup');
    list.setAttribute('aria-label', label ? text(label) : (select.getAttribute('aria-label') || 'Options'));

    var items = opts.map(function (opt) {
      var item = el('button', 'hw-opt__item',
        '<span class="hw-opt__dot" aria-hidden="true"></span>' +
        '<span class="hw-opt__text">' + esc(text(opt)) + '</span>' +
        '<span class="hw-opt__meta"></span>');
      item.type = 'button';
      item.setAttribute('role', 'radio');
      item.setAttribute('data-value', opt.value);
      item.addEventListener('click', function () { self.pick(group, opt.value); });
      list.appendChild(item);
      return item;
    });

    var group = {
      wrapper: wrapper,
      select: select,
      list: list,
      items: items,
      index: select.getAttribute('data-index') || '',
      isVariant: select.hasAttribute('data-variant-input'),
      collapsible: collapsible,
      wrap: null,
      current: null
    };

    var mount = select.closest('.variant-input-wrap') || select.parentNode;
    select.classList.add('hw-opt-native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    if (collapsible) {
      var wrap = el('div', 'hw-opt-wrap');
      var current = el('button', 'hw-opt-current',
        '<span class="hw-opt__dot" aria-hidden="true"></span>' +
        '<span class="hw-opt-current__text"></span>' +
        '<span class="hw-opt-current__change">Change <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg></span>');
      current.type = 'button';
      current.setAttribute('aria-expanded', 'false');
      var panel = el('div', 'hw-opt-panel');
      var inner = el('div');
      var scroll = el('div', 'hw-opt-scroll');

      if (n > 10) {
        // a <div>, not a <label>: the theme styles every label inside a picker as a button
        var filter = el('div', 'hw-opt-filter',
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>' +
          '<input type="search" placeholder="Filter ' + n + ' options…" aria-label="Filter options" autocomplete="off">');
        var input = $('input', filter);
        var empty = el('p', 'hw-opt-empty', 'No options match.');
        empty.hidden = true;
        input.addEventListener('input', function () {
          var q = input.value.toLowerCase().trim();
          var shown = 0;
          items.forEach(function (item) {
            var hit = !q || text(item).toLowerCase().indexOf(q) !== -1;
            item.hidden = !hit;
            if (hit) shown++;
          });
          empty.hidden = shown > 0;
        });
        // typing here must never submit the product form or reach the keeper
        input.addEventListener('keydown', function (e) { if (e.key === 'Enter') e.preventDefault(); });
        inner.appendChild(filter);
        scroll.appendChild(empty);
      }

      scroll.appendChild(list);
      inner.appendChild(scroll);
      panel.appendChild(inner);
      wrap.appendChild(current);
      wrap.appendChild(panel);
      mount.appendChild(wrap);

      current.addEventListener('click', function () {
        var open = !wrap.classList.contains('is-open');
        wrap.classList.toggle('is-open', open);
        current.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) {
          // bring the current choice into view inside the list (never the page)
          var sel = $('.hw-opt__item.is-selected', list);
          if (sel) {
            var delta = sel.getBoundingClientRect().top - scroll.getBoundingClientRect().top;
            scroll.scrollTop = Math.max(0, scroll.scrollTop + delta - 56);
          }
        }
      });
      group.wrap = wrap;
      group.current = current;
    } else {
      mount.appendChild(list);
    }

    wrapper.classList.add('hw-has-opt');
    return group;
  };

  OptionCards.prototype.pick = function (group, value) {
    var select = group.select;
    if (select.value !== value) {
      select.value = value;
      if (group.isVariant && !this.hydrated && this.dirty.indexOf(select) === -1) this.dirty.push(select);
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    group.wrapper.classList.add('is-touched');
    if (group.wrap) {
      group.wrap.classList.remove('is-open');
      group.current.setAttribute('aria-expanded', 'false');
    }
    this.sync();
  };

  OptionCards.prototype.currentValues = function () {
    var vals = {};
    $$('[data-variant-input]', live()).forEach(function (input) {
      var idx = input.getAttribute('data-index');
      if (!idx) return;
      if (input.type === 'radio' || input.type === 'checkbox') {
        if (input.checked) vals[idx] = input.value;
      } else {
        vals[idx] = input.value;
      }
    });
    return vals;
  };

  OptionCards.prototype.findVariant = function (vals) {
    var keys = Object.keys(vals);
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      var ok = true;
      for (var k = 0; k < keys.length; k++) {
        if (v[keys[k]] !== vals[keys[k]]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  OptionCards.prototype.sync = function () {
    var self = this;
    var vals = this.currentValues();
    var current = this.findVariant(vals);

    this.groups.forEach(function (group) {
      var select = group.select;
      var selectedText = '';

      group.items.forEach(function (item, i) {
        var opt = select.options[i];
        if (!opt) return;
        var selected = opt.value === select.value;
        var meta = $('.hw-opt__meta', item);
        var metaText = '';
        var metaClass = '';
        var out = false;

        if (group.isVariant && group.index && self.variants.length) {
          var probe = {};
          Object.keys(vals).forEach(function (k) { probe[k] = vals[k]; });
          probe[group.index] = opt.value;
          var cand = self.findVariant(probe);
          if (!cand) {
            // this combination does not exist — the theme will move to the
            // closest one that does, so leave it selectable without a price
            out = false;
          } else if (!cand.available) {
            out = true;
            metaText = 'Sold out';
          } else if (current && !selected) {
            var diff = cand.price - current.price;
            if (diff > 0) { metaText = '+' + money(diff); metaClass = 'is-plus'; }
            else if (diff < 0) { metaText = money(diff); metaClass = 'is-minus'; }
          }
        }
        // the theme disables <option>s it considers unavailable
        if (opt.disabled && !selected) { out = true; if (!metaText) metaText = 'Unavailable'; }

        item.classList.toggle('is-selected', selected);
        item.classList.toggle('is-out', out);
        item.setAttribute('aria-checked', selected ? 'true' : 'false');
        item.disabled = !!(opt.disabled && !selected);
        meta.textContent = metaText;
        meta.className = 'hw-opt__meta' + (metaClass ? ' ' + metaClass : '');

        if (selected) selectedText = text(opt);
      });

      if (group.current) {
        $('.hw-opt-current__text', group.current).textContent = selectedText || 'Choose an option';
      }
    });

    doc.dispatchEvent(new CustomEvent('hw:options:sync'));
  };

  /* =========================================================================
     B. ORDER CHECKLIST
     ========================================================================= */
  function Checklist(container) {
    this.form = $('form.product-single__form', container);
    this.btn = this.form ? $('[data-add-to-cart]', this.form) : null;
    if (!this.form || !this.btn) return;

    this.node = el('div', 'hw-guide',
      '<div class="hw-guide__top"><span class="hw-guide__title">Order checklist</span>' +
      '<span class="hw-guide__count"><b data-done>0</b> of <span data-total>0</span> complete</span></div>' +
      '<div class="hw-guide__bar"><span class="hw-guide__fill"></span></div>' +
      '<button type="button" class="hw-guide__next"><span data-kicker>Next</span><b data-next></b>' +
      '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg></button>');
    this.node.hidden = true;
    this.node.setAttribute('aria-live', 'polite');
    this.form.parentNode.insertBefore(this.node, this.form);

    var self = this;
    $('.hw-guide__next', this.node).addEventListener('click', function () { self.goToNext(); });

    var schedule = function () {
      window.clearTimeout(self.timer);
      self.timer = window.setTimeout(function () { self.update(); }, 60);
    };
    doc.addEventListener('input', schedule, true);
    doc.addEventListener('change', schedule, true);
    doc.addEventListener('variant:change', schedule);
    // fields are shown / required by other scripts, and the core charge box can
    // be ticked for the customer when the fee is already in their cart
    window.setInterval(function () { self.update(); }, 1500);
    this.update();
  }

  Checklist.prototype.labelFor = function (control) {
    if (control.classList.contains('hw-core-charge__input')) return 'Acknowledge the core charge';
    if (control.classList.contains('hw-freight-charge__input')) return 'Acknowledge the freight charge';
    // build options (snippets/hw-order-options.liquid) carry their own name
    var named = control.closest('[data-hw-field]');
    if (named && named.getAttribute('data-hw-field')) return named.getAttribute('data-hw-field');
    var label = control.id ? $('label[for="' + control.id.replace(/"/g, '') + '"]', doc) : null;
    if (!label) label = control.closest('label');
    var t = '';
    if (label) {
      var strong = $('strong', label);
      t = text(strong || label);
    }
    t = t.replace(/\*+/g, '').replace(/\s*—\s*Optional\s*$/i, '').replace(/[:\s]+$/, '').trim();
    if (!t) t = control.getAttribute('aria-label') || control.getAttribute('placeholder') || 'Required field';
    return t.length > 64 ? t.slice(0, 61) + '…' : t;
  };

  Checklist.prototype.items = function () {
    var self = this;
    var out = [];
    var seen = [];
    var controls = Array.prototype.slice.call(this.form.elements);
    // acknowledgment boxes are not always associated with the form
    $$('.hw-core-charge__input, .hw-freight-charge__input', live()).forEach(function (c) {
      if (controls.indexOf(c) === -1) controls.push(c);
    });

    controls.forEach(function (c) {
      if (!c || !c.tagName) return;
      var tag = c.tagName;
      if (tag !== 'INPUT' && tag !== 'SELECT' && tag !== 'TEXTAREA') return;
      if (c.disabled || c.type === 'hidden' || c.type === 'submit' || c.type === 'button') return;
      var gate = c.classList.contains('hw-core-charge__input') || c.classList.contains('hw-freight-charge__input');
      if (!c.required && !gate) return;
      if (c.name === 'id' || c.name === 'quantity') return;
      // hidden by another script (display:none ancestor) = not currently asked for
      if (!gate && c.offsetParent === null && window.getComputedStyle(c).position !== 'fixed') return;
      if (c.type === 'radio') {
        if (seen.indexOf(c.name) !== -1) return;
        seen.push(c.name);
      }
      var done;
      if (c.type === 'checkbox') done = c.checked;
      // radios can sit outside the <form> element (form= attribute), so look through its controls
      else if (c.type === 'radio') done = controls.some(function (r) { return r.type === 'radio' && r.name === c.name && r.checked; });
      else done = String(c.value || '').trim() !== '' && (!c.validity || c.validity.valid);
      out.push({ control: c, done: done, gate: gate });
    });
    return out;
  };

  Checklist.prototype.update = function () {
    var items = this.items();
    var total = items.length;
    var done = items.filter(function (i) { return i.done; }).length;
    var next = null;
    for (var i = 0; i < items.length; i++) { if (!items[i].done) { next = items[i]; break; } }

    this.state = { total: total, done: done, next: next };

    // per-field check marks
    items.forEach(function (item) {
      var field = item.control.closest('.hw-tuning-field');
      if (field) field.classList.toggle('hw-is-done', item.done && item.control.type !== 'checkbox');
    });

    this.node.classList.toggle('hw-guide--single', total === 1);
    if (!total || (total === 1 && !next)) {
      // nothing to ask for, or the one acknowledgment is done
      this.node.hidden = true;
    } else {
      this.node.hidden = false;
      $('[data-done]', this.node).textContent = done;
      $('[data-total]', this.node).textContent = total;
      $('.hw-guide__fill', this.node).style.transform = 'scaleX(' + (done / total).toFixed(3) + ')';
      var ready = !next;
      this.node.classList.toggle('is-ready', ready);
      $('[data-kicker]', this.node).textContent = ready ? 'Ready' : 'Next';
      $('[data-next]', this.node).textContent = ready ? 'Everything we need is filled in' : this.labelFor(next.control);
      $('.hw-guide__next', this.node).tabIndex = ready ? -1 : 0;
    }
    doc.dispatchEvent(new CustomEvent('hw:checklist:update', { detail: this.state }));
  };

  Checklist.prototype.nextLabel = function () {
    return this.state && this.state.next ? this.labelFor(this.state.next.control) : '';
  };

  Checklist.prototype.goToNext = function () {
    if (!this.state || !this.state.next) return false;
    var control = this.state.next.control;
    var target = control.closest('.hw-core-charge, .hw-freight-charge, .hw-tuning-field, .variant-wrapper') || control;
    var rect = target.getBoundingClientRect();
    var offset = Math.max(120, window.innerHeight * 0.28);
    window.scrollTo({ top: Math.max(0, window.pageYOffset + rect.top - offset), behavior: reduce ? 'auto' : 'smooth' });
    target.classList.remove('hw-nudge');
    // restart the pulse
    void target.offsetWidth;
    target.classList.add('hw-nudge');
    window.setTimeout(function () { target.classList.remove('hw-nudge'); }, 1400);
    window.setTimeout(function () {
      try { control.focus({ preventScroll: true }); } catch (e) { try { control.focus(); } catch (e2) {} }
    }, reduce ? 0 : 420);
    return true;
  };

  /* =========================================================================
     C. STICKY BUY BAR
     ========================================================================= */
  function BuyBar(container, checklist) {
    this.checklist = checklist;
    this.form = $('form.product-single__form', container);
    this.btn = this.form ? $('[data-add-to-cart]', this.form) : null;
    this.priceEl = $('[data-product-price]', container);
    if (!this.form || !this.btn) return;

    var title = container.getAttribute('data-product-title') || text($('.product-single__title', container));
    var img = $('[data-product-photos] img, .product__main-photos img, .product-main-slide img', container);
    var src = img ? (img.currentSrc || img.getAttribute('src') || '') : '';

    this.node = el('div', 'hw-buybar',
      (src ? '<img class="hw-buybar__img" src="' + esc(src) + '" alt="" loading="lazy">' : '') +
      '<div class="hw-buybar__info"><span class="hw-buybar__title">' + esc(title) + '</span>' +
      '<span class="hw-buybar__sub"></span></div>' +
      '<span class="hw-buybar__price"></span>' +
      '<button type="button" class="hw-buybar__btn"></button>');
    this.node.setAttribute('role', 'region');
    this.node.setAttribute('aria-label', 'Quick add to cart');
    this.node.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(this.node);

    this.sub = $('.hw-buybar__sub', this.node);
    this.price = $('.hw-buybar__price', this.node);
    this.action = $('.hw-buybar__btn', this.node);

    var self = this;
    this.action.addEventListener('click', function () { self.onClick(); });

    // show only after the customer has scrolled PAST the real button.
    // (Measured on scroll rather than with an IntersectionObserver: a jump —
    // an anchor link, "Product details", browser back — can move the button
    // from below the screen to above it without it ever being "seen".)
    var ticking = false;
    var check = function () {
      ticking = false;
      var rect = self.btn.getBoundingClientRect();
      if (!rect.width && !rect.height) { self.toggle(false); return; }
      var past = rect.bottom < 0;

      // Long order forms: Add to Cart can be several screens BELOW the first
      // question. Once the customer is working through the form, keep the
      // price and their progress in view.
      var inForm = false;
      var state = self.checklist && self.checklist.state;
      if (!past && rect.top > window.innerHeight && state && state.total >= 3) {
        var start = $('.product-block--variant-picker, .product-block--tuning-fields', live());
        inForm = !!start && start.getBoundingClientRect().top < window.innerHeight * 0.3;
      }
      self.toggle(past || inForm);
    };
    var onScroll = function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(check); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    check();

    var refresh = function () { window.setTimeout(function () { self.render(); }, 30); };
    doc.addEventListener('variant:change', refresh);
    doc.addEventListener('hw:checklist:update', refresh);
    doc.addEventListener('hw:options:sync', refresh);
    doc.addEventListener('change', refresh, true);
    if ('MutationObserver' in window) {
      new MutationObserver(refresh).observe(this.btn, { attributes: true, childList: true, subtree: true, characterData: true });
      if (this.priceEl) new MutationObserver(refresh).observe(this.priceEl, { childList: true, subtree: true, characterData: true });
    }
    this.render();

    // the chat bubble loads late; note when it is there so the bar can keep
    // its corner clear on phones (see hw-ux.css)
    var chatTries = 0;
    var chatTimer = window.setInterval(function () {
      chatTries++;
      if (doc.querySelector('shopify-chat, #shopify-chat, inbox-online-store-chat')) {
        doc.body.classList.add('hw-has-chat');
        window.clearInterval(chatTimer);
      } else if (chatTries > 20) {
        window.clearInterval(chatTimer);
      }
    }, 700);
  }

  BuyBar.prototype.toggle = function (on) {
    if (on === this.isOn) return;
    this.isOn = on;
    this.node.classList.toggle('is-on', on);
    this.node.setAttribute('aria-hidden', on ? 'false' : 'true');
    doc.body.classList.toggle('hw-buybar-on', on);
  };

  BuyBar.prototype.summary = function () {
    var parts = [];
    $$('.variant-wrapper', live()).forEach(function (wrapper) {
      var select = $('select[data-variant-input]', wrapper);
      if (select) {
        var opt = select.options[select.selectedIndex];
        if (opt) parts.push(text(opt));
        return;
      }
      var radio = $('input[type="radio"][data-variant-input]:checked', wrapper);
      if (radio) parts.push(radio.value);
    });
    return parts.join(' · ');
  };

  BuyBar.prototype.render = function () {
    var btnText = text($('[data-add-to-cart-text]', this.btn)) || 'Add to cart';
    var gated = !!(this.btn.getAttribute('data-hw-gates') || '').trim();
    var state = this.checklist && this.checklist.state;
    var todo = !!(state && state.next);

    if (this.priceEl) this.price.innerHTML = this.priceEl.innerHTML;

    this.action.classList.remove('is-todo', 'is-out', 'is-presale');
    if (todo) {
      this.sub.textContent = 'Next: ' + this.checklist.nextLabel();
      this.sub.classList.add('is-todo');
      this.action.textContent = state.total > 1 ? 'Finish order (' + state.done + '/' + state.total + ')' : 'Finish order';
      this.action.classList.add('is-todo');
    } else {
      this.sub.textContent = this.summary();
      this.sub.classList.remove('is-todo');
      this.action.textContent = btnText;
      if (this.btn.disabled && !gated) this.action.classList.add('is-out');
      if (this.btn.classList.contains('btn--pre-sale')) this.action.classList.add('is-presale');
    }
  };

  BuyBar.prototype.onClick = function () {
    // 1. something still needs an answer — take the customer there
    if (this.checklist && this.checklist.goToNext()) return;

    // 2. the real button is disabled (sold out, or a gate we could not name):
    //    show it to the customer rather than doing anything on their behalf
    if (this.btn.disabled) {
      var target = this.btn.closest('.product-block') || this.btn;
      var rect = target.getBoundingClientRect();
      window.scrollTo({ top: Math.max(0, window.pageYOffset + rect.top - window.innerHeight * 0.45), behavior: reduce ? 'auto' : 'smooth' });
      return;
    }

    // 3. press the real Add to Cart button — every existing check still runs
    this.btn.click();
  };

  /* =========================================================================
     D. SAVINGS TAG TIDY-UP
     The theme rewrites the savings text on every variant change using its own
     money formatter ("$20<sup>00</sup> Off"); bring it back to "$20 Off".
     ========================================================================= */
  function tidySavings(container) {
    var tag = $('[data-save-price]', container);
    if (!tag || !('MutationObserver' in window)) return;
    var busy = false;
    function clean() {
      if (busy) return;
      var raw = tag.innerHTML;
      if (!raw || raw.indexOf('<') === -1 && !/[.,]00/.test(raw)) return;
      var out = raw
        .replace(/<sup>\s*00\s*<\/sup>/gi, '')
        .replace(/<sup>\s*(\d\d)\s*<\/sup>/gi, '.$1')
        .replace(/<[^>]*>/g, '')
        .replace(/[.,]00(?!\d)/, '')
        .replace(/\s+/g, ' ')
        .trim();
      if (out !== raw) {
        busy = true;
        tag.textContent = out;
        busy = false;
      }
    }
    new MutationObserver(clean).observe(tag, { childList: true, characterData: true, subtree: true });
    clean();
  }

  /* ── jump link to the details section, when the template has one ─────────── */
  function initJump() {
    var story = doc.getElementById('hw-story');
    $$('[data-hw-jump]').forEach(function (link) {
      if (!story) return;
      link.hidden = false;
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var top = window.pageYOffset + story.getBoundingClientRect().top - 90;
        window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
      });
    });
  }

  ready(function () {
    var container = mainProduct();
    if (!container) return;
    var checklist = null;
    try { new OptionCards(container); } catch (e) { if (window.console) console.warn('[hw] option cards', e); }
    try { checklist = new Checklist(container); if (!checklist.node) checklist = null; } catch (e) { checklist = null; if (window.console) console.warn('[hw] checklist', e); }
    try { new BuyBar(container, checklist); } catch (e) { if (window.console) console.warn('[hw] buy bar', e); }
    try { tidySavings(container); } catch (e) {}
    try { initJump(); } catch (e) {}
  });
})();
